import * as LucideIcons from 'lucide-react';

// Get all icon names dynamically from Lucide
export const getAllLucideIconNames = (): string[] => {
  const allKeys = Object.keys(LucideIcons);
  
  const filtered = allKeys.filter(
    (key) => {
      // Filter for icon exports - all Lucide exports are objects, not functions
      const isNotInternal = !['createLucideIcon', 'Icon', 'default'].includes(key);
      // Keep only the NON-Icon suffixed versions (e.g., AArrowDown) for clean names
      const isNotIconSuffix = !key.endsWith('Icon');
      
      return isNotInternal && isNotIconSuffix;
    }
  ).sort();
  
  return filtered;
};

// Get icon component by name with fallback
export const getIconComponent = (iconName: string) => {
  // Try the name as-is first (could be Icon suffixed already)
  let IconComponent = LucideIcons[iconName as keyof typeof LucideIcons];
  
  // If not found and doesn't end with Icon, try adding Icon suffix
  if (!IconComponent && !iconName.endsWith('Icon')) {
    IconComponent = LucideIcons[`${iconName}Icon` as keyof typeof LucideIcons];
  }
  
  // If still not found and ends with Icon, try removing Icon suffix
  if (!IconComponent && iconName.endsWith('Icon')) {
    const baseName = iconName.slice(0, -4); // Remove 'Icon' suffix
    IconComponent = LucideIcons[baseName as keyof typeof LucideIcons];
  }
  
  return IconComponent || LucideIcons.HelpCircle; // fallback to HelpCircle if icon not found
};

// Get categorized icons for better organization
export const getCategorizedIcons = () => {
  const allIcons = getAllLucideIconNames();
  
  return {
    popular: [
      'Star', 'Award', 'Badge', 'Crown', 'Trophy', 'Medal', 'Shield', 'CheckCircle',
      'Heart', 'ThumbsUp', 'Zap', 'Flame', 'Gem', 'Target', 'Users', 'Sparkles'
    ].filter(icon => allIcons.includes(icon)),
    
    badges: allIcons.filter(name => 
      /^(Award|Badge|Crown|Trophy|Medal|Shield|Star|Ribbon|Rosette)/.test(name) ||
      name.toLowerCase().includes('badge') ||
      name.toLowerCase().includes('award') ||
      name.toLowerCase().includes('medal')
    ),
    
    shapes: allIcons.filter(name => 
      /^(Circle|Square|Triangle|Diamond|Hexagon|Octagon|Pentagon|Heart|Star)/.test(name) ||
      name.toLowerCase().includes('circle') ||
      name.toLowerCase().includes('square') ||
      name.toLowerCase().includes('triangle')
    ),
    
    symbols: allIcons.filter(name => 
      /^(Check|Cross|Plus|Minus|Equal|Hash|At|Percent|Dollar|Pound|Euro|Yen)/.test(name) ||
      name.toLowerCase().includes('check') ||
      name.toLowerCase().includes('plus') ||
      name.toLowerCase().includes('symbol')
    ),
    
    people: allIcons.filter(name => 
      /^(User|Users|Person|People|Team|Group|Contact|Account|Profile)/.test(name) ||
      name.toLowerCase().includes('user') ||
      name.toLowerCase().includes('people') ||
      name.toLowerCase().includes('team')
    ),
    
    nature: allIcons.filter(name => 
      /^(Sun|Moon|Cloud|Leaf|Tree|Flower|Mountain|Wave|Fire|Flame|Zap|Snowflake)/.test(name) ||
      name.toLowerCase().includes('sun') ||
      name.toLowerCase().includes('tree') ||
      name.toLowerCase().includes('leaf')
    ),
    
    business: allIcons.filter(name => 
      /^(Building|Store|Shop|Bank|Factory|Office|Briefcase|Calendar|Clock|Graph)/.test(name) ||
      name.toLowerCase().includes('building') ||
      name.toLowerCase().includes('business') ||
      name.toLowerCase().includes('office')
    )
  };
};

// Get badge-suitable icons (curated list for better UX)
export const getBadgeSuitableIcons = (): string[] => {
  try {
    const categorized = getCategorizedIcons();
    
    // Combine the most relevant categories for badges
    const combined = [
      ...categorized.popular,
      ...categorized.badges,
      ...categorized.shapes.slice(0, 10), // Limit shapes to most common ones
      ...categorized.symbols.slice(0, 8), // Limit symbols to most relevant ones
    ].filter((icon, index, array) => array.indexOf(icon) === index); // Remove duplicates
    
    // Fallback to a basic list if categorization fails
    if (combined.length === 0) {
      const allIcons = getAllLucideIconNames();
      return allIcons.slice(0, 50); // Just return first 50 icons as fallback
    }
    
    return combined;
  } catch (error) {
    // Emergency fallback
    return ['Star', 'Award', 'Badge', 'Crown', 'Trophy', 'Medal', 'Shield', 'Heart', 'ThumbsUp', 'CheckCircle'];
  }
};

// Search icons by name with fuzzy matching
export const searchIcons = (icons: string[], searchTerm: string): string[] => {
  if (!searchTerm.trim()) return icons;
  
  const term = searchTerm.toLowerCase();
  
  return icons.filter(iconName => {
    const name = iconName.toLowerCase();
    // Exact match gets priority
    if (name.includes(term)) return true;
    
    // Fuzzy matching for typos
    const words = name.split(/(?=[A-Z])/); // Split camelCase
    return words.some(word => word.toLowerCase().includes(term));
  }).sort((a, b) => {
    // Sort by relevance: exact matches first, then partial matches
    const aName = a.toLowerCase();
    const bName = b.toLowerCase();
    const aExact = aName.includes(term);
    const bExact = bName.includes(term);
    
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;
    return aName.localeCompare(bName);
  });
};

// Validate if an icon name exists
export const isValidIconName = (iconName: string): boolean => {
  // Check if the name exists as-is (Lucide exports are objects, not functions)
  if (iconName in LucideIcons) {
    return true;
  }
  
  // Check with Icon suffix if not already present
  if (!iconName.endsWith('Icon')) {
    const withSuffix = `${iconName}Icon`;
    return withSuffix in LucideIcons;
  }
  
  // Check without Icon suffix if present
  if (iconName.endsWith('Icon')) {
    const withoutSuffix = iconName.slice(0, -4);
    return withoutSuffix in LucideIcons;
  }
  
  return false;
};